<template>
  <div class="container">
    <div class="is-centered">
      <div>
        <ValidationObserver
          v-slot="{ errors }"
          tag="div"
        >
          <input-form
            ref="form"
            :errors="errors"
            name="PathwayChange"
            @submit.prevent="submitApplicationInfo"
          >
            <h1>TFA Pathway Change</h1>
            <div class="columns">
              <div class="column">
                <vee-radio
                  v-model="ApplicantPropertyOwner"
                  :options="['Actual property owner', 'Authorized Representative']"
                  label="Who is filling out this application?"
                  rules="required"
                  required
                  error="This field is required"
                />
              </div>
            </div>
            <div v-if="ApplicantPropertyOwner == 'Authorized Representative'">
              <h3>
                Authorized Representative Completing Application - Contact Information
              </h3>
              <div class="columns">
                <div class="column">
                  <vee-textbox
                    id="property-management-representative-companyname"
                    v-model="
                      PropertyManagementRepresentative.ContactInformation
                        .CompanyName"
                    mode="eager"
                    name="txt-property-management-representative-companyname"
                    placeholder="Company Name*"
                    rules="required"
                  />
                </div>
              </div>
              <div class="columns">
                <div class="column">
                  <vee-textbox
                    id="property-management-representative-first-name"
                    v-model="
                      PropertyManagementRepresentative.ContactInformation
                        .FirstName
                    "
                    mode="eager"
                    name="txt-property-management-representative-first-name"
                    placeholder="First Name"
                    rules="required|whitespace"
                    required
                    error="This field is required"
                  />
                </div>
                <div class="column">
                  <vee-textbox
                    id="property-management-representative-last-name"
                    v-model="
                      PropertyManagementRepresentative.ContactInformation
                        .LastName
                    "
                    mode="eager"
                    name="txt-property-management-representative-last-name"
                    placeholder="Last Name"
                    rules="required|whitespace"
                    required
                    error="This field is required"
                  />
                </div>
              </div>
              <div class="columns">
                <div class="column">
                  <vee-textbox
                    id="property-management-representative-phone"
                    v-model="
                      PropertyManagementRepresentative.ContactInformation
                        .CellPhone
                    "
                    mode="eager"
                    name="txt-property-management-representative-cell-phone"
                    placeholder="Cell Phone Number (for text message notifications &amp; updates)*"
                    type="tel"
                    rules="required|phone"
                  />
                </div>
                <div class="column">
                  <vee-textbox
                    id="property-management-representative-email"
                    v-model="
                      PropertyManagementRepresentative.ContactInformation
                        .EmailAddress
                    "
                    mode="eager"
                    name="txt-property-management-representative-email-address"
                    placeholder="Email Address*"
                    rules="required|email"
                  />
                </div>
              </div>
            </div>
            <div class="columns">
              <div class="column">
                <vee-radio
                  v-model="Landlord.ComplaintFiled"
                  :name="`tfa-complaint-filed-yesno`"
                  :options="MultipleChoiceFieldOptions.YesNo"
                  label="Have you filed a landlord tenant complaint in Municipal Court within the past 12 months against this household for this unit?"
                  rules="required"
                  required
                  error="This field is required"
                  @click="onNo()"
                />
              </div><!--add on no to clear subfields-->
            </div>
            <div
              v-if="Landlord.ComplaintFiled === 'Yes'"
              class="columns"
            >
              <div class="column">
                <callout type="danger">
                  <b>Any landlord/tenant complaint filed against this household for this unit since {{ DateApplicationCreated.toLocaleDateString("en-US", { timeZone: "UTC" }) }} renders parties ineligible for Targeted Financial Assistance. Parties may be eligible for mediation.</b>
                </callout>
                <vee-textbox
                  v-model="Landlord.LTFilingNumber"
                  mode="eager"
                  :name="`txt-lt-filing-number`"
                  placeholder="Please enter the LT Filing number"
                  rules="required"
                  required
                  error="This field is required"
                  maxlength="250"
                /> <!--make numeric, check char limit-->
              </div>
            </div>
            <div class="columns">
              <div class="column">
                <vee-radio
                  v-model="Landlord.TFAPrevious"
                  :name="`tfa-previous-multiple-choice`"
                  :options="MultipleChoiceFieldOptions.YesNo"
                  label="Have you received Targeted Financial Assistance (TFA) for this tenant(s) and this unit since January 26, 2023? TFA is a one-time payment per household per unit from the City to a landlord to cover back rent or fees (arrears). More information about TFA can be found at https://eviction-diversion.phila.gov/FAQ."
                  rules="required"
                  required
                  error="This field is required"
                  @click="onNo()"
                />
              </div>
            </div>
            <div
              v-if="Landlord.TFAPrevious === 'Yes'"
              class="columns"
            >
              <div class="column">
                <callout type="danger">
                  <b>CAUTION: Selecting 'Yes' to this question makes parties INELIGIBLE for Targeted Financial Assistance (TFA). Parties may be eligible for mediation.</b>
                </callout>
              </div>
            </div>
            <div
              v-if="Landlord.TFAPrevious === 'No'"
              class="columns"
            >
              <div class="column">
                <vee-radio
                  v-model="Landlord.RentOrFeesOwed"
                  :options="MultipleChoiceFieldOptions.YesNo"
                  label="Are you seeking back rent or fees owed?"
                  :name="`rent-or-fees-radio`"
                  rules="required"
                  required
                  error="This field is required"
                  @click="onNo()"
                />
              </div>
            </div>
            <div
              v-if="Landlord.RentOrFeesOwed === 'Yes'"
              class="columns"
            >
              <div class="column">
                <vee-textbox
                  v-model="Landlord.AmountOwedInArrears"
                  label="Arrears amount must be $1 or greater."
                  mode="eager"
                  :name="`txt-amount-owed-in-arrears`"
                  placeholder="What is the amount owed?"
                  rules="required|minimumArrears|doubleCurrencyArrears:2"
                  type="number"
                  required
                  error="This field is required"
                  maxlength="250"
                  min="1"
                  @keyup="onNo()"
                />
              </div>
            </div>
            <div v-if="Landlord.RentOrFeesOwed == 'No'">
              <callout type="danger">
                <b>Back rent or fees must be owed to qualify for TFA.</b>
              </callout>
            </div>
            <div v-if="Landlord.RentOrFeesOwed == 'Yes' && Arrears != null">
              <callout type="warning">
                The original submission arrear amount for {{ applicantId }} was ${{ Arrears }}. Arrears amount must be between $1 to $3,500 to be eligible for TFA.
              </callout>
            </div>

            <div
              v-if="Landlord.RentOrFeesOwed === 'Yes' && Landlord.AmountOwedInArrears > 0 && Landlord.AmountOwedInArrears <= 3500 && Landlord.AmountOwedInArrears != null"
              class="columns"
            >
              <div class="column">
                <vee-radio
                  id="tfa-optin"
                  v-model="Landlord.TFAOptIn"
                  :name="`tfa-optin-multiple-radio`"
                  :options="MultipleChoiceFieldOptions.YesNo"
                  label="If eligible, will you apply for and accept Targeted Financial Assistance (TFA) funds to cover the back rent and fees that are owed?"
                  rules="required"
                  required
                  error="This field is required"
                  @click="onNo()"
                />
              </div>
            </div>
          
            <callout
              v-if="Landlord.TFAOptIn === 'Yes'"
              type="info"
            >
              <ul>
                <li>By selecting "Yes", your application will be reviewed for TFA eligibility. TFA payments are made directly to the landlord to cover a tenant's past-due rent and fees. Conditions apply - please see this affidavit for a complete list of conditions.</li>
                <br>
                <li>These conditions include:</li>
                <ul>
                  <li>Providing a signed copy of the landlord affidavit, proof of ownership, W-9 and bank account information (routing number, account number fields and canceled check or statement). (You will be prompted to upload these documents on the 2nd page of this application.)</li>
                  <li>Not seeking eviction for two months after the last month that TFA funds cover;</li>
                  <li>Agreeing that the TFA award will clear the tenant's debt.</li>
                </ul>
              </ul>
            </callout>
             
            <callout
              v-if="Landlord.TFAOptIn == 'No'"
              type="info"
            >
              By selecting "No," your application will not be considered for TFA. You will remain on your existing pathway. You must continue to participate in the Program for at least 30 days from the date of your approved application before seeking to evict through court. You may not file until after {{ (ThirtyDaysFromApproval != null) ? ThirtyDaysFromApproval.toLocaleDateString("en-US") : "" }}.
            </callout>
                
            <template #submit>
              <div class="column is-2 is-offset-10">
                <button
                  type="button"
                  :disabled="Landlord.AmountOwedInArrears > 3500 || Landlord.AmountOwedInArrears == null || SubmitStatus.Value === SubmitStatus.Options.PENDING"
                  class="button is-primary"
                  @click="submitApplicationInfo"
                >
                  Submit
                </button>
              </div>
              <div class="column">
                <div v-if="SubmitStatus.Value === SubmitStatus.Options.PENDING">
                  Submitting...
                </div>
                <div v-if="SubmitStatus.Value === SubmitStatus.Options.OK">
                  OK
                </div>
                <div v-if="SubmitStatus.Value === SubmitStatus.Options.ERROR">
                  Error
                </div>
              </div>
            </template>
          </input-form>
        </validationobserver>
      </div>
    </div>
  </div>
</template>
    
<script>
import options from "@/utilities/options";
import buildLandlordDataFetchObject from "@/utilities/data-objects/landlord-data-fetch-object";
import buildTFAPathwayChangeDataObject from "@/utilities/data-objects/tfa-pathway-change-object";
import axios from "axios";
import {
  getPropertyValueOrDefault,
  dataObjectToXml,
} from "@/utilities/functions";
import { Callout } from "@phila/phila-ui";
    
export default {
  name: "PathwayChange",
  components: { Callout },
  mixins: [options],
  props:{
    appid: { default: null, type: String },
  },
  data() {
    return {
      ApplicantPropertyOwner: null,
      Arrears: null,
      LandlordRecordID: null,
      applicantId: null,
      ThirtyDaysFromApproval: null,
      DateApplicationCreated: null,
      Landlord: { ComplaintFiled: null, LTFilingNumber: null, TFAPrevious: null, RentOrFeesOwed: null, AmountOwedInArrears: null },
      PropertyManagementRepresentative: {
        ContactInformation: {
          FirstName: null,
          LastName: null,
          CellPhone: null,
          EmailAddress: null,
          CompanyName: null,
        },
      },
    };
  },
  created(){
    var vData = this;
    let dataObject = buildLandlordDataFetchObject(Number(vData.appid), "TFA_RESUBMISSION_PAGE_GET_LANDLORD_DATA");
    let requestObj = {
      table: process.env.VUE_APP_QB_APPLICATION,
      data: dataObject,
    };
    axios
      .post(
        "https://djql8jp3sb.execute-api.us-east-1.amazonaws.com/V1/EDP-TFA-login-QuickBase-Request-Proxy-Reads",
        requestObj,
      )
      .then((response) => {
        const responseObject = getPropertyValueOrDefault(
          ["data", "record", "f"],
          response,
        );
        console.log(response);
        vData.DateApplicationCreated =  getPropertyValueOrDefault(["_text"], responseObject.find(i => i._attributes.id === "1"));
        vData.DateApplicationCreated = new Date(Number(vData.DateApplicationCreated));
        vData.DateApplicationCreated.setDate(vData.DateApplicationCreated.getDate() - 365);
        vData.applicantId =  getPropertyValueOrDefault(["_text"], responseObject.find(i => i._attributes.id === "62"));
        vData.Arrears =  getPropertyValueOrDefault(["_text"], responseObject.find(i => i._attributes.id === "65"));
        if(getPropertyValueOrDefault(["_text"], responseObject.find(i => i._attributes.id === "121")) != null){
          vData.ThirtyDaysFromApproval = new Date(Number(getPropertyValueOrDefault(["_text"], responseObject.find(i => i._attributes.id === "121"))));
        }
      }).catch((error) => {
        console.log(error);
      });
  },
  methods: {
    async submitApplicationInfo(){
      var vData = this;
      vData.SubmitStatus.Value = vData.SubmitStatus.Options.PENDING;
      const isInputValid = await vData.$refs.form.$parent.validate();
      if (isInputValid) {
        try{
          const dataObject = await buildTFAPathwayChangeDataObject(vData);
          const xmlDataObject = await dataObjectToXml(dataObject);
          const requestObject = {
            table: process.env.VUE_APP_QB_APPLICATION,
            action: "API_EditRecord",
            data: xmlDataObject,
          };
            
          await axios
            .post(
              "https://djql8jp3sb.execute-api.us-east-1.amazonaws.com/V1/Eviction-Diversion-login-QuickBase-Request-Proxy",
              requestObject,
            )
            .then((qbResult) => {
              vData.SubmitStatus.Value = vData.SubmitStatus.Options.OK;
            }).catch(err => {
              vData.SubmitStatus.Value = vData.SubmitStatus.Options.ERROR;
            });
        } catch(err){
          vData.SubmitStatus.Value = vData.SubmitStatus.Options.ERROR;
        }
        vData.$router.push({
          name: "DetailedApplication",
          params: { applicantId: vData.applicantId },
        });
      } else {
        vData.SubmitStatus.Value = vData.SubmitStatus.Options.ERROR;
      }
    },
    onNo() {
      //   if(this.Landlord.IsResidentialProperty === 'No') {
      //     this.Landlord.HasNoticeofDivisionRights = null;
      //     this.norFileUpload = [];
      //   }
      if(this.Landlord.ComplaintFiled === 'No') {
        this.Landlord.LTFilingNumber = null;
      }
      if(this.Landlord.RentOrFeesOwed === 'No'){
        this.Landlord.AmountOwedInArrears = null;
        //this.Landlord.TFAPrevious = null;
      }
      // if(this.Landlord.LeaseTermMoveout === 'No'){
      //     this.Landlord.ReasonForMoveout = null;
      //   }if ( this.Landlord.OtherIssuesToBeResolved === 'No'){
      //     this.Landlord.DescribeOtherIssues = null;
      //   }
      if(this.Landlord.AmountOwedInArrears > 3500){
        this.Landlord.TFAOptIn = null;
        //this.Landlord.TFAPrevious = null;
      }
    },
  },
};
</script>